/**
 * 左栏「图层与元素」的数据源 —— 只读派生，不做缓存（由 Vue 侧按版本号 + 虚拟滚动兜住性能）。
 */
import { walkEditable, type Path } from '../dom';

export type GroupKey =
  | 'circle'
  | 'ellipse'
  | 'rect'
  | 'line'
  | 'polyline'
  | 'polygon'
  | 'path'
  | 'text'
  | 'image'
  | 'g'
  | 'other';

/** 分组顺序固定（原型定稿）：图形 → 文本 → 分组，与 SVG 文档序无关 */
export const GROUP_ORDER: readonly GroupKey[] = [
  'circle',
  'ellipse',
  'rect',
  'line',
  'polyline',
  'polygon',
  'path',
  'text',
  'image',
  'g',
  'other',
];

export const GROUP_LABEL: Record<GroupKey, string> = {
  circle: 'Circles',
  ellipse: 'Ellipses',
  rect: 'Rects',
  line: 'Lines',
  polyline: 'Polylines',
  polygon: 'Polygons',
  path: 'Paths',
  text: 'Texts',
  image: 'Images',
  g: 'Groups',
  other: 'Others',
};

export interface ListItem {
  element: Element;
  path: Path;
  /** 稳定 key（path 序列化），供虚拟列表复用 DOM */
  key: string;
  tag: string;
  id: string | null;
  label: string;
  swatch: string | null;
}

export interface ListGroup {
  key: GroupKey;
  label: string;
  items: ListItem[];
  /** 未过滤时的总数（搜索时组头仍显示原始计数） */
  total: number;
}

export interface ListModel {
  items: ListItem[];
  groups: ListGroup[];
  total: number;
}

const SNIPPET_MAX = 42;

const groupKeyOf = (tag: string): GroupKey =>
  (GROUP_ORDER as readonly string[]).includes(tag) ? (tag as GroupKey) : 'other';

/** 色块颜色：fill 优先，其次 stroke；无填充/渐变纹理（url(...)）→ null（UI 画灰） */
export function swatchOf(el: Element): string | null {
  const pick = (v: string | null): string | null =>
    !v || v === 'none' || v === 'transparent' || v.startsWith('url(') ? null : v;
  return pick(el.getAttribute('fill')) ?? pick(el.getAttribute('stroke')) ?? null;
}

/** 行标签：有 id 显示 #id；text 无 id 显示文本片段；其余显示 <tag> */
export function labelOf(el: Element): string {
  const id = el.getAttribute('id');
  if (id) return `#${id}`;
  const tag = el.tagName.toLowerCase();
  if (tag === 'text') {
    const text = (el.textContent ?? '').replace(/\s+/g, ' ').trim();
    if (text) return text.length > SNIPPET_MAX ? `${text.slice(0, SNIPPET_MAX)}…` : text;
  }
  return `<${tag}>`;
}

export function buildList(root: Element): ListModel {
  const items: ListItem[] = [];
  walkEditable(root, (element, path) => {
    const tag = element.tagName.toLowerCase();
    items.push({
      element,
      path,
      key: path.join('-'),
      tag,
      id: element.getAttribute('id'),
      label: labelOf(element),
      swatch: swatchOf(element),
    });
  });

  const buckets = new Map<GroupKey, ListItem[]>();
  for (const item of items) {
    const key = groupKeyOf(item.tag);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(item);
    else buckets.set(key, [item]);
  }

  const groups = GROUP_ORDER.filter((k) => buckets.has(k)).map((k) => {
    const list = buckets.get(k) as ListItem[];
    return { key: k, label: GROUP_LABEL[k], items: list, total: list.length };
  });

  return { items, groups, total: items.length };
}

export function matchesQuery(item: ListItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    item.tag.includes(q) ||
    (item.id ?? '').toLowerCase().includes(q) ||
    item.label.toLowerCase().includes(q)
  );
}

/** 搜索过滤：丢弃空组，但保留 total（组头计数不被搜索结果改写） */
export function filterList(groups: ListGroup[], query: string): ListGroup[] {
  if (!query.trim()) return groups;
  return groups
    .map((g) => ({ ...g, items: g.items.filter((i) => matchesQuery(i, query)) }))
    .filter((g) => g.items.length > 0);
}
